import React, { useMemo } from "react";
import { FaArrowRight, FaSearch } from "react-icons/fa";
import Avatar from "./Avatar";
import EmptyState from "./EmptyState";
import ProgressBar from "./ProgressBar";
import { CLASS_BTN_PRIMARY } from "./reportingStyles";

function TeamSearchInput({ value, onChange, className = "" }) {
  return (
    <div className={`relative w-full sm:w-52 ${className}`}>
      <FaSearch
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
        size={11}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search employees…"
        className="daily-report-input w-full rounded-md py-1.5 pl-8 pr-3 text-xs text-slate-700"
      />
    </div>
  );
}

function TeamReporting({ team = [], onView, search = "" }) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return team;
    return team.filter((emp) => emp.name?.toLowerCase().includes(q));
  }, [team, search]);

  if (team.length === 0) {
    return <EmptyState message="No one has reported activity for this date" />;
  }

  if (filtered.length === 0) {
    return <EmptyState message="No employees match your search" />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Employee</th>
            <th className="px-3 py-2">Report</th>
            <th className="px-3 py-2">Completion</th>
            <th className="px-3 py-2">Remaining</th>
            <th className="px-3 py-2">Last reported</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((emp) => {
            const remaining = (emp.in_progress || 0) + (emp.not_started || 0);
            const submitted = Boolean(emp.last_reported);
            return (
              <tr key={emp.user_id} className="border-t border-slate-100 hover:bg-indigo-50/30">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Avatar name={emp.name} size={28} />
                    <span className="font-medium text-slate-800">{emp.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  {submitted ? (
                    <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      Submitted
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-10 shrink-0 text-xs text-slate-700">
                      {emp.completed}/{emp.total}
                    </span>
                    <ProgressBar
                      value={emp.total ? Math.round((emp.completed / emp.total) * 100) : 0}
                      width="w-14"
                    />
                  </div>
                </td>
                <td className="px-3 py-2.5 text-sm font-medium text-amber-600">{remaining}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{emp.last_reported || "-"}</td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    onClick={() => onView?.(emp.user_id)}
                    className={CLASS_BTN_PRIMARY}
                  >
                    View report <FaArrowRight size={9} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { TeamReporting, TeamSearchInput };
export default TeamReporting;